import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg } from "@/lib/api";
import { isoDate } from "@/lib/format";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtPts = (n) => Math.round(n ?? 0).toLocaleString("it-IT");
const monthLabel = (m) => `${MONTHS[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`;
const shift = (m, k) => { const n = Number(m.slice(0, 4)) * 12 + Number(m.slice(5)) - 1 + k; return `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, "0")}`; };
const DAY_NOTE = { SUN: "Domenica", HOLIDAY: "Festività", ABSENT: "Assenza approvata" };
const noSat = (days) => days.filter((d) => d.kind !== "SAT");

const Stat = ({ label, value, sub, tone = "", testId }) => (
  <div className="border bg-white p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-display text-2xl font-bold ${tone}`} data-testid={`${testId}-value`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Progress = ({ c }) => {
  const pct = c.target ? Math.min((c.counted / c.target) * 100, 100) : 0;
  const mark = c.target ? Math.min((c.target_to_date / c.target) * 100, 100) : 0;
  const ahead = c.counted >= c.target_to_date;
  return (
    <div className="border bg-white p-4" data-testid="tech-prod-progress">
      <div className="flex items-baseline justify-between text-sm">
        <span><b data-testid="tech-prod-counted">{fmtPts(c.counted)}</b> / {fmtPts(c.target)} punti obiettivo</span>
        <span className="font-mono text-xs">{Math.round(pct)}%</span>
      </div>
      <div className="relative mt-2 h-3 bg-zinc-200">
        <div className={`h-3 transition-[width] duration-700 ${c.counted >= c.target ? "bg-emerald-600" : "bg-[#0B0F14]"}`} style={{ width: `${pct}%` }} />
        {c.target_to_date > 0 && c.target_to_date < c.target && <div className="absolute -top-1 h-5 w-0.5 bg-amber-500" style={{ left: `${mark}%` }} title="Obiettivo a oggi" />}
      </div>
      <div className="mt-2 text-xs text-muted-foreground" data-testid="tech-prod-pace">
        {c.remaining > 0 ? <>Mancano <b className="text-foreground">{fmtPts(c.remaining)}</b> punti all'obiettivo · {c.work_days} giorni lavorativi nel mese</> : <span className="font-semibold text-emerald-700">Obiettivo raggiunto!</span>}
        {c.target_to_date > 0 && c.remaining > 0 && <> · {ahead ? "in linea" : `${fmtPts(c.target_to_date - c.counted)} punti sotto il ritmo`}</>}
      </div>
    </div>
  );
};

const DailyChart = ({ c }) => (
  <div className="border bg-white p-3" data-testid="tech-prod-daily">
    <div className="mb-2 text-sm font-semibold">Punti giorno per giorno</div>
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={noSat(c.days).map((d) => ({ ...d, label: Number(d.date.slice(8)) }))} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v) => [`${fmtPts(v)} punti`, "Prodotti"]} labelFormatter={(l, p) => { const d = p?.[0]?.payload; return d ? `${d.date.split("-").reverse().join("/")}${DAY_NOTE[d.kind] ? ` · ${DAY_NOTE[d.kind]}` : ""}` : l; }} />
          <ReferenceLine y={c.daily_target} stroke="#f59e0b" strokeDasharray="4 3" ifOverflow="extendDomain" />
          <Bar dataKey="points">{noSat(c.days).map((d) => <Cell key={d.date} fill={d.points >= c.daily_target ? "#059669" : "#0B0F14"} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="mt-1 text-[11px] text-muted-foreground">Linea arancione: obiettivo giornaliero {fmtPts(c.daily_target)} punti</div>
  </div>
);

const DayList = ({ c }) => (
  <div className="border bg-white" data-testid="tech-prod-days">
    {noSat([...c.days].reverse()).filter((d) => d.points > 0 || d.kind === "WORK").filter((d) => d.date <= isoDate(new Date())).map((d) => (
      <div key={d.date} className="flex items-center justify-between border-b px-3 py-2 text-sm last:border-0" data-testid={`tech-prod-day-${d.date}`}>
        <span><span className="font-mono">{d.date.split("-").reverse().slice(0, 2).join("/")}</span>{DAY_NOTE[d.kind] && <span className="ml-2 text-xs text-muted-foreground">{DAY_NOTE[d.kind]}</span>}</span>
        <span className="text-right"><b className={d.kind === "WORK" && d.points >= c.daily_target ? "text-emerald-700" : ""}>{fmtPts(d.points)}</b> pt{d.pending > 0 && <span className="block text-[11px] text-amber-700">{fmtPts(d.pending)} da validare</span>}</span>
      </div>
    ))}
  </div>
);

const History = ({ rows, onPick }) => (
  <div className="border bg-white" data-testid="tech-prod-history">
    <div className="grid grid-cols-4 border-b bg-muted/40 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground"><span>Mese</span><span className="text-right">Punti</span><span className="text-right">Obiettivo</span><span className="text-right">KPI</span></div>
    {rows.map((h) => (
      <button key={h.month} onClick={() => onPick(h.month)} className="grid w-full grid-cols-4 border-b px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-muted/40" data-testid={`tech-prod-history-${h.month}`}>
        <span>{monthLabel(h.month)}</span>
        <span className="text-right font-semibold">{fmtPts(h.counted)}{h.incentive > 0 && <span className="block text-[11px] font-normal text-emerald-700">+{fmtPts(h.incentive)} incentivo</span>}</span>
        <span className={`text-right ${h.target && h.counted >= h.target ? "text-emerald-700" : ""}`}>{fmtPts(h.target)}</span>
        <span className="text-right">{h.kpi == null ? "—" : `${h.kpi}%`}</span>
      </button>
    ))}
  </div>
);

export default function TechProduction() {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/tech/production", { params: month ? { month } : {} }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [month]);
  if (error) return <div className="border bg-white p-4 text-sm text-red-700" data-testid="tech-prod-error">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground" data-testid="tech-prod-loading">Caricamento…</div>;
  const c = data.current;
  return (
    <div className="space-y-4" data-testid="tech-production">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Produzione</h1>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shift(c.month, -1))} className="p-2" aria-label="Mese precedente" data-testid="tech-prod-prev"><ChevronLeft className="h-5 w-5" /></button>
          <span className="min-w-[84px] text-center text-sm font-semibold" data-testid="tech-prod-month">{monthLabel(c.month)}</span>
          <button onClick={() => setMonth(shift(c.month, 1))} disabled={data.is_current_month} className="p-2 disabled:opacity-30" aria-label="Mese successivo" data-testid="tech-prod-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {data.is_current_month && c.days.find((d) => d.date === isoDate(new Date()))?.kind !== "SAT" && <Stat label="Oggi" value={`${fmtPts(c.days.find((d) => d.date === isoDate(new Date()))?.points)} pt`} sub={`obiettivo ${fmtPts(c.daily_target)}`} testId="tech-prod-today" />}
        <Stat label="Punti del mese" value={fmtPts(c.counted)} sub={c.pending > 0 ? `${fmtPts(c.pending)} da validare` : "tutti validati"} testId="tech-prod-month-points" />
        <Stat label="Resa KPI" value={c.kpi == null ? "—" : `${c.kpi}%`} sub={`${c.completed_positive} positivi su ${c.assigned} assegnati`} tone={c.kpi >= 90 ? "text-emerald-700" : ""} testId="tech-prod-kpi" />
        <Stat label="Incentivo maturato" value={`${fmtPts(c.incentive)} pt`} sub={`${c.incentive_pct}% dei punti oltre l'obiettivo`} tone={c.incentive > 0 ? "text-emerald-700" : ""} testId="tech-prod-incentive" />
      </div>
      <Progress c={c} />
      <DailyChart c={c} />
      <DayList c={c} />
      <h2 className="pt-2 text-base font-semibold">Mesi precedenti</h2>
      <History rows={data.history} onPick={setMonth} />
    </div>
  );
}
